import { useEventCallback } from '@cian/react-utils';
import { ModalFullscreen } from '@cian/ui-kit/modal';
import * as React from 'react';

import { MapPreview } from '../../components/MapPreview';
import { openMap, ApplicationContext, OfferContext, useFragmentStoreContext, setCenter, closeMap } from '../../context';
import { IMapFragmentProps, IApplicationContext, ICoordinates } from '../../types';
import { CloseFullscreenMapButtonContainer } from '../CloseFullscreenMapButtonContainer';
import { ErrorBoundary } from '../ErrorBoundary';
import { MapContainerLoadable } from '../MapContainer/MapContainerLoadable';
import { getMapPreviewUrl } from './utils';

export interface IMapFragmentContainerProps extends IMapFragmentProps {
  context: IApplicationContext;
  onMapPreviewClick?: VoidFunction;
  onMapOpen?: VoidFunction;
}

export function MapFragment({
  apiKey,
  suggestApiKey,
  houseId,
  regionId,
  coordinates,
  context,
  infrastructureCategories,
  onMapPreviewClick,
  onMapOpen,
}: IMapFragmentContainerProps) {
  const {
    state: { isOpen },
    dispatch,
  } = useFragmentStoreContext();

  const houseContext = React.useMemo(
    () => ({
      regionId,
      realtyOfferId: houseId,
      coordinates: coordinates as ICoordinates,
    }),
    [coordinates, houseId, regionId],
  );

  const openMapModal = React.useCallback(() => {
    if (coordinates) {
      dispatch(setCenter(coordinates));
    }

    dispatch(openMap());

    if (onMapOpen) {
      onMapOpen();
    }
  }, [coordinates, dispatch, onMapOpen]);

  const handlePreviewClick = useEventCallback(() => {
    openMapModal();

    if (onMapPreviewClick) {
      onMapPreviewClick();
    }
  });

  const closeMapModal = useEventCallback(() => {
    dispatch(closeMap());
  });

  const mapPreviewLink = React.useMemo(
    () => getMapPreviewUrl({ config: context.config, coordinates, id: houseId }),
    [context.config, coordinates, houseId],
  );

  if (!coordinates) {
    return null;
  }

  return (
    <ErrorBoundary logger={context.logger}>
      <ApplicationContext.Provider value={context}>
        <MapPreview link={mapPreviewLink} onClick={handlePreviewClick} />
        <ModalFullscreen open={isOpen} theme="white" onClose={closeMapModal}>
          <OfferContext.Provider value={houseContext}>
            <MapContainerLoadable
              apiKey={apiKey}
              suggestApiKey={suggestApiKey}
              infrastructureCategories={infrastructureCategories}
            />
          </OfferContext.Provider>
          <CloseFullscreenMapButtonContainer />
        </ModalFullscreen>
      </ApplicationContext.Provider>
    </ErrorBoundary>
  );
}
